import React, { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Callout from '../ui/Callout';
import OtpModal from './OtpModal';
import { useT } from '../../i18n/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { confirmPayoutNumber, requestPayoutNumberOtp } from '../../api/payouts';
import { formatRelativeTime } from '../../utils/format';
import { PROVIDERS, providerLabel } from './constants';

const emptyForm = { msisdn: '', name: '', providerId: '' };

/**
 * Shows the configured payout destination and owns the two-step
 * "change number" flow (form → e-mailed OTP). Shared by the Payouts and
 * Settings pages so both stay in sync with the same rules.
 */
export default function PayoutDestinationCard({
  msisdn,
  name,
  providerId,
  updatedAt,
  canManage,
  mailConfigured,
  onChanged,
}) {
  const t = useT();
  const toast = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [payload, setPayload] = useState(null);

  const disabledReason = !canManage
    ? t('payouts_page.admin_only')
    : !mailConfigured
      ? t('payouts_page.mail_missing')
      : null;

  const openForm = () => {
    setForm({ msisdn: msisdn || '', name: name || '', providerId: providerId || '' });
    setErrors({});
    setFormOpen(true);
  };

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    const digits = form.msisdn.replace(/[\s-]/g, '');
    if (!digits) next.msisdn = t('validation.required');
    else if (!/^\+?\d{9,15}$/.test(digits)) next.msisdn = t('validation.invalid_phone');
    if (!form.name.trim()) next.name = t('validation.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleRequest = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    const next = {
      msisdn: form.msisdn.replace(/[\s-]/g, ''),
      name: form.name.trim(),
      providerId: form.providerId || null,
    };
    setSubmitting(true);
    try {
      const issued = await requestPayoutNumberOtp(next);
      setPayload(next);
      setChallenge(issued);
      setFormOpen(false);
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    const issued = await requestPayoutNumberOtp(payload);
    setChallenge(issued);
    toast.info(t('otp.resent'));
  };

  const handleConfirm = async (code) => {
    const overview = await confirmPayoutNumber({ challengeId: challenge.challengeId, code });
    setChallenge(null);
    setPayload(null);
    toast.success(t('payouts_page.toast_number_updated'));
    onChanged?.(overview);
  };

  const providerOptions = [
    { value: '', label: t('payouts_page.provider_auto') },
    ...PROVIDERS.map((p) => ({ value: p.id, label: t(`payouts_page.providers.${p.key}`) })),
  ];

  return (
    <Card
      title={t('payouts_page.destination_title')}
      subtitle={t('payouts_page.destination_subtitle')}
      className="payout-destination"
    >
      {msisdn ? (
        <div className="payout-destination__body">
          <div className="payout-destination__icon" aria-hidden>📱</div>
          <div className="payout-destination__info">
            <div className="payout-destination__number">{msisdn}</div>
            <div className="payout-destination__name">{name || '—'}</div>
            <div className="payout-destination__meta">
              {providerLabel(t, providerId)}
              {updatedAt && <> · {t('payouts_page.updated', { when: formatRelativeTime(updatedAt, t) })}</>}
            </div>
          </div>
        </div>
      ) : (
        <Callout tone="warning" title={t('payouts_page.no_number_title')}>
          {t('payouts_page.no_number_body')}
        </Callout>
      )}

      <div className="payout-destination__actions">
        <Button variant={msisdn ? 'ghost' : 'gold'} onClick={openForm} disabled={Boolean(disabledReason)}>
          {msisdn ? t('payouts_page.change_number') : t('payouts_page.set_number')}
        </Button>
        {disabledReason && <div className="form-hint">{disabledReason}</div>}
      </div>

      <Modal
        isOpen={formOpen}
        onClose={submitting ? () => {} : () => setFormOpen(false)}
        title={msisdn ? t('payouts_page.change_number') : t('payouts_page.set_number')}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={submitting}>{t('common.cancel')}</Button>
            <Button variant="gold" onClick={handleRequest} loading={submitting}>{t('otp.send_code')}</Button>
          </>
        )}
      >
        <form onSubmit={handleRequest} noValidate>
          <Callout tone="info">{t('payouts_page.number_change_notice')}</Callout>
          <FormField label={t('payouts_page.field_msisdn')} htmlFor="payout-msisdn" required error={errors.msisdn} hint={t('payouts_page.msisdn_hint')}>
            <Input id="payout-msisdn" value={form.msisdn} onChange={set('msisdn')} invalid={Boolean(errors.msisdn)} placeholder="2507XXXXXXXX" inputMode="tel" />
          </FormField>
          <FormField label={t('payouts_page.field_name')} htmlFor="payout-name" required error={errors.name} hint={t('payouts_page.name_hint')}>
            <Input id="payout-name" value={form.name} onChange={set('name')} invalid={Boolean(errors.name)} maxLength={120} />
          </FormField>
          <FormField label={t('payouts_page.field_provider')} htmlFor="payout-provider">
            <Select id="payout-provider" options={providerOptions} value={form.providerId} onChange={set('providerId')} />
          </FormField>
        </form>
      </Modal>

      <OtpModal
        isOpen={Boolean(challenge)}
        onClose={() => { setChallenge(null); setPayload(null); }}
        title={t('payouts_page.confirm_number_title')}
        intro={payload ? (
          <div className="otp-summary">
            <div className="detail-item-label">{t('payouts_page.new_destination')}</div>
            <div className="otp-summary__value">{payload.msisdn} · {payload.name}</div>
          </div>
        ) : null}
        challenge={challenge}
        onConfirm={handleConfirm}
        onResend={handleResend}
        confirmLabel={t('payouts_page.confirm_number')}
      />
    </Card>
  );
}
